import React from "react";
import { Chip, useTheme, alpha } from "@mui/material";
import Category from "../../models/finance/Category";
import { CategoryType } from "../../models/finance/CategoryType";

interface Props {
  category: Category;
  onClick: () => void;
}

const CategoryItem: React.FC<Props> = ({ category, onClick }) => {
  const theme = useTheme();
  
  // Determine colors based on category type
  const isExpense = category.type === CategoryType.Expense;
  const baseColor = isExpense ? theme.palette.error : theme.palette.success;
  
  return (
    <Chip
      label={category.title}
      onClick={onClick}
      variant={category.isActive ? "filled" : "outlined"}
      sx={{
        // Base styling
        fontWeight: 600,
        fontSize: '0.875rem',
        height: 36,
        borderRadius: 3,
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        
        // Active category styling
        ...(category.isActive && {
          backgroundColor: baseColor.main,
          color: 'white',
          border: `1px solid ${baseColor.main}`,
          '&:hover': {
            backgroundColor: baseColor.dark,
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(baseColor.main, 0.3)}`,
          },
        }),
        
        // Inactive category styling
        ...(!category.isActive && {
          backgroundColor: 'transparent',
          color: theme.palette.text.disabled,
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.04),
            borderColor: theme.palette.text.secondary,
            color: theme.palette.text.secondary,
            transform: 'translateY(-1px)',
          },
        }),
        
        // Focus styling
        '&:focus': {
          outline: `2px solid ${alpha(baseColor.main, 0.5)}`,
          outlineOffset: 2,
        },
        
        // Active state
        '&:active': {
          transform: 'translateY(0)',
        },
      }}
    />
  );
};

export default CategoryItem;
